import React, { useState } from 'react';

const RoomDetails = ({ 
  roomDetails = [], 
  roomWiseCancellationPolicy = {}, 
  onBookNow, 
  isProfileComplete,
  isSessionExpired = false,
  hasValidSession = true
}) => {
  const [expandedRooms, setExpandedRooms] = useState({});
  const [showCompleteRoomDetails, setShowCompleteRoomDetails] = useState({});
  const [expandedCancellationPolicies, setExpandedCancellationPolicies] = useState({});

  const toggleRoomExpansion = (roomIndex) => {
    setExpandedRooms(prev => ({
      ...prev,
      [roomIndex]: !prev[roomIndex]
    }));
  };

  const toggleCompleteRoomDetails = (roomIndex) => {
    setShowCompleteRoomDetails(prev => ({
      ...prev,
      [roomIndex]: !prev[roomIndex]
    }));
  };

  // Helper function to format date and time from backend
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return null;
    
    try {
      // First try to parse as a Date object
      const date = new Date(dateTimeString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        // If date parsing fails, try to handle common backend formats
        // Format: "2024-01-15 14:30:00" or "2024-01-15T14:30:00"
        const cleanedString = dateTimeString.replace('T', ' ');
        const [datePart, timePart] = cleanedString.split(' ');
        
        return {
          date: datePart || dateTimeString,
          time: timePart || '',
          raw: dateTimeString
        };
      }
      
      return {
        date: date.toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }),
        time: date.toLocaleTimeString('en-IN', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        }),
        raw: dateTimeString
      };
    } catch (error) {
      // Fallback to original string if any error occurs
      const [datePart, timePart] = dateTimeString.split(' ');
      return {
        date: datePart || dateTimeString,
        time: timePart || '',
        raw: dateTimeString
      };
    }
  };

  const toggleCancellationPolicy = (roomIndex) => {
    setExpandedCancellationPolicies(prev => ({
      ...prev,
      [roomIndex]: !prev[roomIndex]
    }));
  };

  if (!roomDetails || roomDetails.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Available Rooms</h2>
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Rooms Available</h3>
          <p className="text-gray-600">No rooms are currently available for this hotel.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Available Rooms</h2>
        <p className="text-gray-600 text-sm">Choose from our available room options</p>
      </div>
      
      {roomDetails.map((room, index) => {
        const isExpanded = expandedRooms[index];
        const showComplete = showCompleteRoomDetails[index];
        const showAllPolicies = expandedCancellationPolicies[index];
        const hasMultipleRooms = room.roomNames && room.roomNames.length > 2;
        
        return (
          <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-200">
            <div className="p-4">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                {/* Room Info - Takes 5 columns */}
                <div className="lg:col-span-5">
                  {/* Room Names */}
                  <div className="mb-4">
                    <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                      <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                        <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 2L3 7v11a2 2 0 002 2h4v-6h2v6h4a2 2 0 002-2V7l-7-5z" />
                        </svg>
                      </div>
                      Room Options
                      {room.roomNames && room.roomNames.length > 0 && (
                        <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-bold px-2 py-1 rounded-full">
                          {room.roomNames.length} types
                        </span>
                      )}
                    </h3>
                    
                    {room.roomNames && room.roomNames.length > 0 ? (
                      <div className="space-y-2">
                        {/* Always show first 2 rooms */}
                        {room.roomNames.slice(0, 2).map((name, nameIndex) => (
                          <div key={nameIndex} className="bg-blue-50 rounded-lg p-2 border border-blue-100">
                            <div className="flex items-center justify-between">
                              <span className="text-gray-800 font-medium text-sm">{name}</span>
                              <span className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded">
                                Room {nameIndex + 1}
                              </span>
                            </div>
                          </div>
                        ))}

                        {/* Show remaining rooms if expanded or if there are only a few more */}
                        {hasMultipleRooms && (
                          <>
                            {/* Collapsible container for additional rooms */}
                            <div className={`transition-all duration-300 overflow-hidden ${
                              isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                            }`}>
                              <div className="space-y-2 pt-2">
                                {room.roomNames.slice(2).map((name, nameIndex) => (
                                  <div key={nameIndex + 2} className="bg-blue-50 rounded-lg p-2 border border-blue-100">
                                    <div className="flex items-center justify-between">
                                      <span className="text-gray-800 font-medium text-sm">{name}</span>
                                      <span className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded">
                                        Room {nameIndex + 3}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Toggle button */}
                            <button
                              onClick={() => toggleRoomExpansion(index)}
                              className="w-full mt-2 py-2 px-3 bg-blue-100 hover:bg-blue-200 text-blue-700 text-sm font-medium rounded-lg transition-colors duration-200 flex items-center justify-center"
                            >
                              {isExpanded ? (
                                <>
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                  </svg>
                                  Show Less
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                  Show {room.roomNames.length - 2} More Room Types
                                </>
                              )}
                            </button>
                          </>
                        )}

                        {/* Alternative: Scrollable container for many rooms */}
                        {room.roomNames.length > 6 && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">All Room Types:</h4>
                            <div className="max-h-32 overflow-y-auto space-y-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                              {room.roomNames.map((name, nameIndex) => (
                                <div key={nameIndex} className="flex items-center justify-between bg-white rounded p-2 text-xs">
                                  <span className="text-gray-700 font-medium">{name}</span>
                                  <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded text-xs">
                                    #{nameIndex + 1}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm italic">Room details not available</p>
                    )}
                  </div>

                  {/* Compact Room Features */}
                  <div className="space-y-3">
                    {/* Meal Plan */}
                    {room.impDetails?.mealType && (
                      <div className="bg-orange-50 rounded-lg p-3 border border-orange-100">
                        <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center">
                          <div className="w-5 h-5 bg-orange-100 rounded-full flex items-center justify-center mr-2">
                            <svg className="w-3 h-3 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4z" />
                            </svg>
                          </div>
                          Meals Included
                        </h4>
                        <div className="flex flex-wrap gap-1">
                          {room.impDetails.mealType.split(',').slice(0, 3).map((meal, mealIndex) => (
                            <span key={mealIndex} className="bg-white text-orange-700 text-xs font-medium px-2 py-1 rounded border">
                              {meal.trim()}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Room Features Grid */}
                    <div className="bg-gray-50 rounded-lg p-3">
                      <h4 className="text-sm font-bold text-gray-900 mb-2">Room Features</h4>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex items-center">
                          <div className="w-4 h-4 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                            <svg className="w-2 h-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className={`font-medium ${room.impDetails?.isRefundable ? 'text-green-700' : 'text-red-700'}`}>
                            {room.impDetails?.isRefundable ? 'Refundable' : 'Non-refundable'}
                          </span>
                        </div>
                        
                        <div className="flex items-center">
                          <div className="w-4 h-4 bg-yellow-100 rounded-full flex items-center justify-center mr-2">
                            <svg className="w-2 h-2 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className="font-medium text-gray-700">
                            {room.impDetails?.withTransfers ? 'Transfers Inc.' : 'No Transfers'}
                          </span>
                        </div>

                        {room.impDetails?.payAt && (
                          <div className="flex items-center col-span-2">
                            <div className="w-4 h-4 bg-green-100 rounded-full flex items-center justify-center mr-2">
                              <svg className="w-2 h-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <span className="font-medium text-gray-700 text-xs">{room.impDetails.payAt}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* View Complete Room Details Button */}
                    <div className="mt-3">
                      <button
                        onClick={() => toggleCompleteRoomDetails(index)}
                        className="w-full py-2 px-3 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors duration-200 flex items-center justify-center"
                      >
                        {showComplete ? (
                          <>
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                            Hide Complete Details
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                            View Complete Room Details
                          </>
                        )}
                      </button>
                    </div>

                    {/* Complete Room Details Section */}
                    {showComplete && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                        <h4 className="text-sm font-bold text-gray-900 mb-3">Complete Room Information</h4>
                        
                        <div className="space-y-3">
                          {/* Room Specifications - Only show if room has specifications */}
                          {room.specifications && (
                            <div className="bg-white rounded-lg p-3">
                              <h5 className="text-xs font-semibold text-gray-900 mb-2 flex items-center">
                                <svg className="w-3 h-3 text-blue-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M10 2L3 7v11a2 2 0 002 2h4v-6h2v6h4a2 2 0 002-2V7l-7-5z" />
                                </svg>
                                Room Specifications
                              </h5>
                              <div className="grid grid-cols-2 gap-1 text-xs">
                                {room.specifications.roomSize && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Room Size:</span>
                                    <span className="font-medium text-gray-900">{room.specifications.roomSize}</span>
                                  </div>
                                )}
                                {room.specifications.bedType && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Bed Type:</span>
                                    <span className="font-medium text-gray-900">{room.specifications.bedType}</span>
                                  </div>
                                )}
                                {room.specifications.maxOccupancy && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Max Occupancy:</span>
                                    <span className="font-medium text-gray-900">{room.specifications.maxOccupancy}</span>
                                  </div>
                                )}
                                {room.specifications.roomType && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Room Type:</span>
                                    <span className="font-medium text-gray-900">{room.specifications.roomType}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Amenities - Only show if room has amenities */}
                          {room.amenities && room.amenities.length > 0 && (
                            <div className="bg-white rounded-lg p-3">
                              <h5 className="text-xs font-semibold text-gray-900 mb-2 flex items-center">
                                <svg className="w-3 h-3 text-green-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                Room Amenities
                              </h5>
                              <div className="grid grid-cols-2 gap-1 text-xs">
                                {room.amenities.map((amenity, idx) => (
                                  <div key={idx} className="flex items-center">
                                    <svg className="w-2 h-2 text-green-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-gray-700">{amenity}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Detailed Pricing */}
                          <div className="bg-white rounded-lg p-3">
                            <h5 className="text-xs font-semibold text-gray-900 mb-2 flex items-center">
                              <svg className="w-3 h-3 text-purple-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                              </svg>
                              Pricing Breakdown
                            </h5>
                            <div className="space-y-1 text-xs">
                              {room.roomPrice && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Base Room Rate:</span>
                                  <span className="font-medium text-gray-900">₹{new Intl.NumberFormat('en-IN').format(room.roomPrice)}</span>
                                </div>
                              )}
                              {room.crossOutPrice && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Original Price:</span>
                                  <span className="font-medium text-gray-500 line-through">₹{room.crossOutPrice}</span>
                                </div>
                              )}
                              {room.taxesIncluded !== undefined && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Taxes & Fees:</span>
                                  <span className="font-medium text-gray-900">
                                    {room.taxesIncluded ? 'Included' : 'Extra'}
                                  </span>
                                </div>
                              )}
                              {room.totalFare && (
                                <div className="flex justify-between border-t pt-1">
                                  <span className="text-gray-600 font-semibold">Total Amount:</span>
                                  <span className="font-bold text-green-600">₹{new Intl.NumberFormat('en-IN').format(room.totalFare)}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Additional Services - Only show if room has services */}
                          {room.additionalServices && room.additionalServices.length > 0 && (
                            <div className="bg-white rounded-lg p-3">
                              <h5 className="text-xs font-semibold text-gray-900 mb-2 flex items-center">
                                <svg className="w-3 h-3 text-indigo-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                                  <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
                                </svg>
                                Additional Services
                              </h5>
                              <div className="space-y-1 text-xs">
                                {room.additionalServices.map((service, idx) => (
                                  <div key={idx} className="flex justify-between">
                                    <span className="text-gray-600">{service.name}:</span>
                                    <span className="font-medium text-gray-900">{service.availability}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Check-in Instructions */}
                          {room.impDetails?.checkInInstructions && (
                            <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                              <h5 className="text-xs font-semibold text-gray-900 mb-2 flex items-center">
                                <svg className="w-3 h-3 text-yellow-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                Check-in Instructions
                              </h5>
                              <p className="text-xs text-gray-700">{room.impDetails.checkInInstructions}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Enhanced Cancellation Policy - Takes 4 columns */}
                <div className="lg:col-span-4">
                  {room.roomBookingCode && roomWiseCancellationPolicy[room.roomBookingCode] && (
                    <div className="bg-red-50 rounded-lg p-4 border border-red-100 h-fit">
                      <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center">
                        <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center mr-2">
                          <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        Cancellation Policy
                        <span className="ml-2 bg-red-100 text-red-800 text-xs font-bold px-2 py-1 rounded-full">
                          {roomWiseCancellationPolicy[room.roomBookingCode].length} policies
                        </span>
                      </h4>
                      
                      <div className="space-y-3">
                        {/* Show first 2 policies or all if expanded */}
                        {(showAllPolicies 
                          ? roomWiseCancellationPolicy[room.roomBookingCode] 
                          : roomWiseCancellationPolicy[room.roomBookingCode].slice(0, 2)
                        ).map((policy, policyIndex) => (
                          <div key={policyIndex} className="bg-white border border-red-200 rounded-lg p-3 shadow-sm">
                            <div className="space-y-2 text-xs">
                              {/* Policy Period */}
                              <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-lg p-3 border border-red-100">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-semibold text-red-800 text-xs uppercase tracking-wide flex items-center">
                                    <svg className="w-3 h-3 text-red-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    Policy Period
                                  </span>
                                  <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                                    #{policyIndex + 1}
                                  </span>
                                </div>
                                <div className="grid grid-cols-1 gap-2">
                                  {policy.fromDate && (
                                    <div className="flex justify-between items-center">
                                      <span className="font-medium text-gray-600 flex items-center">
                                        <svg className="w-3 h-3 text-green-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        From:
                                      </span>
                                      <div className="text-right" title={`Backend data: ${policy.fromDate}`}>
                                        <div className="text-gray-900 font-semibold text-xs">
                                          {formatDateTime(policy.fromDate)?.date || policy.fromDate}
                                        </div>
                                        {formatDateTime(policy.fromDate)?.time && (
                                          <div className="text-blue-600 font-medium text-xs bg-blue-50 px-2 py-1 rounded flex items-center">
                                            <svg className="w-2 h-2 text-blue-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                            </svg>
                                            {formatDateTime(policy.fromDate).time}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  {policy.toDate && (
                                    <div className="flex justify-between items-center">
                                      <span className="font-medium text-gray-600 flex items-center">
                                        <svg className="w-3 h-3 text-red-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        To:
                                      </span>
                                      <div className="text-right" title={`Backend data: ${policy.toDate}`}>
                                        <div className="text-gray-900 font-semibold text-xs">
                                          {formatDateTime(policy.toDate)?.date || policy.toDate}
                                        </div>
                                        {formatDateTime(policy.toDate)?.time && (
                                          <div className="text-red-600 font-medium text-xs bg-red-50 px-2 py-1 rounded flex items-center">
                                            <svg className="w-2 h-2 text-red-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                            </svg>
                                            {formatDateTime(policy.toDate).time}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Cancellation Fee */}
                              <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                                <span className="font-semibold text-gray-700 flex items-center">
                                  <svg className="w-3 h-3 text-red-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                                  </svg>
                                  Cancellation Fee:
                                </span>
                                <div className="text-right">
                                  <span className="font-bold text-red-600 text-sm">
                                    {policy.chargeType === 'Fixed' 
                                      ? `₹${new Intl.NumberFormat('en-IN').format(policy.cancellationCharge)}`
                                      : `${policy.cancellationCharge}%`
                                    }
                                  </span>
                                  <div className="text-xs text-gray-500">
                                    {policy.chargeType === 'Fixed' ? 'Fixed Amount' : 'of Room Price'}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Additional policy details if available */}
                              {policy.description && (
                                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                                  <p className="text-xs text-yellow-800">{policy.description}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                        
                        {/* See More / See Less Button */}
                        {roomWiseCancellationPolicy[room.roomBookingCode].length > 2 && (
                          <div className="text-center pt-2">
                            <button
                              onClick={() => toggleCancellationPolicy(index)}
                              className="bg-red-100 hover:bg-red-200 text-red-700 px-4 py-2 rounded-lg text-xs font-medium transition-colors duration-200 flex items-center mx-auto"
                            >
                              {showAllPolicies ? (
                                <>
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                  </svg>
                                  Show Less
                                </>
                              ) : (
                                <>
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                  See {roomWiseCancellationPolicy[room.roomBookingCode].length - 2} More Policies
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Pricing and Booking - Takes 3 columns */}
                <div className="lg:col-span-3">
                  <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-lg p-4 h-fit">
                    {/* Price */}
                    <div className="text-center mb-3">
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <div className="text-xs text-gray-500 mb-1">Room Price</div>
                        {room.roomPrice && (
                          <div className="flex items-baseline justify-center mb-1">
                            <span className="text-sm text-gray-500 mr-1">₹</span>
                            <span className="text-xl font-bold text-gray-900">
                              {new Intl.NumberFormat('en-IN').format(room.roomPrice)}
                            </span>
                          </div>
                        )}
                        <p className="text-xs text-gray-500">per night</p>
                      </div>
                    </div>

                    {/* Book Now Button - Conditional based on session status */}
                    {hasValidSession && !isSessionExpired ? (
                      <button
                        onClick={() => onBookNow(room)}
                        className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 px-4 rounded-lg font-bold hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                      >
                        Book This Room
                      </button>
                    ) : (
                      <div className="w-full bg-gradient-to-r from-gray-400 to-gray-500 text-white py-3 px-4 rounded-lg font-bold cursor-not-allowed opacity-75">
                        <div className="flex items-center justify-center">
                          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          Session Expired
                        </div>
                      </div>
                    )}

                    {/* Profile completion warning - Only show if session is valid */}
                    {hasValidSession && !isSessionExpired && !isProfileComplete && (
                      <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                        <p className="text-xs text-yellow-800 text-center font-medium">
                          ⚠️ Complete profile to book
                        </p>
                      </div>
                    )}

                    {/* Session expired message */}
                    {isSessionExpired && (
                      <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded">
                        <div className="text-center">
                          <div className="flex items-center justify-center mb-2">
                            <svg className="w-4 h-4 text-red-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            <span className="text-xs text-red-800 font-semibold">Booking Session Expired</span>
                          </div>
                          <p className="text-xs text-red-700 mb-2">
                            Please refresh the page to start a new booking session
                          </p>
                          <button
                            onClick={() => window.location.reload()}
                            className="bg-red-500 hover:bg-red-600 text-white text-xs font-medium px-3 py-1 rounded transition-colors duration-200 flex items-center mx-auto"
                          >
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Refresh Page
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Trust Indicators */}
                    <div className="mt-3 space-y-1">
                      {[
                        { icon: 'M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z', text: 'Instant confirmation' },
                        { icon: 'M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2z', text: 'Secure booking' },
                        { icon: 'M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z', text: '24/7 support' }
                      ].map((item, idx) => (
                        <div key={idx} className="flex items-center text-xs text-gray-600">
                          <svg className="w-3 h-3 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d={item.icon} clipRule="evenodd" />
                          </svg>
                          {item.text}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RoomDetails;